import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import {
  useRfqBuilderActions,
  useRfqBuilderView,
} from '@paradigm/features/src/unified-rfqs/ui';

import { selectActiveProducts } from '@paradigm/store/auth';
import { useTrackAction } from '@paradigm/features/src/analytics';
import Badge from '@paradigm/design-system/src/components/Badge';
import { isProductCodeDisabledForType } from '@paradigm/features/src/products/domain';
import { PRODUCTS } from '@paradigm/features/src/products/config';
import { useMultiProductVenues } from '@paradigm/features/src/feature-flags/context';
import {
  isMultiProductAllowed,
  MULTI_PRODUCT_OPTION_VALUE,
} from '@paradigm/features/src/unified-rfqs/ui/rfq-builder/multi-product';
import { venueSupportsCustomStrategy } from '@paradigm/features/src/unified-rfqs/entities/products/venue';
import ToggleButton from './components/ToggleButton';

export default function ProductButtons() {
  const activeProducts = useSelector(selectActiveProducts);
  const { productShortcut, auction, venue, product } = useRfqBuilderView();
  const { options: allOptions, selectedOption } = productShortcut;
  const multiProductVenues = useMultiProductVenues();
  // Gated like the sibling shortcuts (user entitled to the venue) and like
  // the dropdown's Multi-Product option (feature allow-list plus a venue
  // that supports custom strategies).
  const showMultiButton =
    activeProducts.includes(venue.selectedOption.value) &&
    isMultiProductAllowed(
      multiProductVenues,
      venue.selectedOption.value,
      auction.selectedOption.value,
    ) &&
    venueSupportsCustomStrategy(venue.selectedOption.value);
  const isMultiActive =
    product.selectedOption.value === MULTI_PRODUCT_OPTION_VALUE;
  const options = allOptions.filter((opt) => {
    const shortcutProduct = PRODUCTS[opt.value];
    return (
      shortcutProduct != null && activeProducts.includes(shortcutProduct.venue)
    );
  });
  const { selectProduct } = useRfqBuilderActions();
  const trackAction = useTrackAction();

  const handleOnChange = useCallback(
    (productCode: string) => {
      return () => {
        trackAction('update product', { productCode });
        selectProduct(productCode);
      };
    },
    [selectProduct, trackAction],
  );

  // Multi-product entry point: seeds a custom structure with two empty
  // legs, each of which picks its own product in the table.
  const handleMultiClick = useCallback(() => {
    trackAction('update product', { productCode: MULTI_PRODUCT_OPTION_VALUE });
    selectProduct(MULTI_PRODUCT_OPTION_VALUE);
  }, [selectProduct, trackAction]);

  return useMemo(
    () => (
      <>
        {options.map((option) => {
          return (
            <ToggleButton
              ariaLabel={`select ${option.label}`}
              key={option.value}
              active={option.value === selectedOption?.value}
              onClick={handleOnChange(option.value)}
              disabled={isProductCodeDisabledForType(
                option.value,
                auction.selectedOption.value,
              )}
            >
              {PRODUCTS[option.value]?.isNew && (
                <Badge variant="new" top={-4} right={-8}>
                  new
                </Badge>
              )}
              {option.label}
            </ToggleButton>
          );
        })}
        {showMultiButton && (
          <ToggleButton
            ariaLabel="select multi-product"
            active={isMultiActive}
            onClick={handleMultiClick}
          >
            Multi
          </ToggleButton>
        )}
      </>
    ),
    [
      auction.selectedOption.value,
      handleOnChange,
      handleMultiClick,
      isMultiActive,
      options,
      selectedOption?.value,
      showMultiButton,
    ],
  );
}
